import { useState, useMemo, useEffect } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// options: [{ value, label }]
// onSearch (optional): async (term) => [{value,label}] — enables server-side search (no client filtering).
// selectedLabel (optional): label to show for the current value when it's not in the current options.
export function SearchableSelect({ value, onValueChange, options = [], onSearch, selectedLabel,
  placeholder = "Seleziona…", searchPlaceholder = "Cerca…", emptyText = "Nessun risultato", testid, className }) {
  const [open, setOpen] = useState(false);
  const [dyn, setDyn] = useState([]);
  const opts = onSearch ? dyn : options;
  const selected = opts.find((o) => o.value === value);
  const triggerLabel = selected ? selected.label : (selectedLabel || options.find((o) => o.value === value)?.label || null);

  const debounced = useMemo(() => {
    let t;
    return (term) => {
      clearTimeout(t);
      t = setTimeout(async () => {
        try { setDyn((await onSearch(term)) || []); } catch { setDyn([]); }
      }, 250);
    };
  }, [onSearch]);

  useEffect(() => { if (onSearch && open) debounced(""); }, [open, onSearch, debounced]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} data-testid={testid}
          className={cn("w-full justify-between font-normal", !triggerLabel && "text-muted-foreground", className)}>
          <span className="truncate">{triggerLabel || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={!onSearch}>
          <CommandInput placeholder={searchPlaceholder} data-testid={testid ? `${testid}-search` : undefined}
            onValueChange={onSearch ? debounced : undefined} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {opts.map((o) => (
                <CommandItem key={o.value} value={o.label}
                  data-testid={testid ? `${testid}-option-${o.value}` : undefined}
                  onSelect={() => { onValueChange(o.value); setOpen(false); }}>
                  <Check className={cn("mr-2 h-4 w-4", value === o.value ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{o.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
